import { toast } from "sonner";
import SEO from "@/components/site/SEO";
import LegalLayout, { LegalSection } from "@/components/site/LegalLayout";

const KEY = "altin_cookie_consent";

const cookieTable = [
  {
    type: "Strictly Necessary",
    purpose: "Enable core functionality such as page navigation and security. The site cannot function properly without them.",
    consent: "Always active",
  },
  {
    type: "Preference",
    purpose: "Remember your choices, such as your cookie consent selection.",
    consent: "Optional",
  },
  {
    type: "Analytics",
    purpose: "Help us understand how visitors interact with the website so we can improve it.",
    consent: "Optional",
  },
];

export default function Cookies() {
  const reset = () => {
    localStorage.removeItem(KEY);
    toast.success("Cookie preferences reset. The banner will reappear.");
    setTimeout(() => window.location.reload(), 900);
  };

  return (
    <>
      <SEO
        title="Cookie Policy"
        description="Altin Link Cookie Policy — how we use cookies and similar technologies, the categories of cookies we use, and how you can manage your consent in line with GDPR."
        path="/cookie-policy"
      />
      <LegalLayout
        testid="cookies-page"
        overline="Cookie Policy"
        title="Cookie"
        accent="Policy"
        intro="This Cookie Policy explains what cookies are, how Altin Link uses them, and how you can control your preferences in compliance with the EU GDPR and the Turkish KVKK."
        updated="December 2025"
      >
        <LegalSection index="01" id="cookies-what" title="What Are Cookies?">
          <p>
            Cookies are small text files placed on your device when you visit a
            website. They are widely used to make websites work efficiently, to
            remember your preferences, and to provide information to site owners.
          </p>
        </LegalSection>

        <LegalSection index="02" id="cookies-types" title="Types of Cookies We Use">
          <div className="overflow-x-auto">
            <table className="w-full text-left border border-altin-border">
              <thead>
                <tr className="bg-altin-surfaceAlt text-altin-ink">
                  <th className="p-4 text-xs uppercase tracking-[0.2em] font-medium">Category</th>
                  <th className="p-4 text-xs uppercase tracking-[0.2em] font-medium">Purpose</th>
                  <th className="p-4 text-xs uppercase tracking-[0.2em] font-medium">Consent</th>
                </tr>
              </thead>
              <tbody>
                {cookieTable.map((row) => (
                  <tr key={row.type} className="border-t border-altin-border align-top">
                    <td className="p-4 font-medium text-altin-ink whitespace-nowrap">{row.type}</td>
                    <td className="p-4 text-altin-muted">{row.purpose}</td>
                    <td className="p-4 text-altin-muted whitespace-nowrap">{row.consent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </LegalSection>

        <LegalSection index="03" id="cookies-consent" title="Your Consent">
          <p>
            When you first visit our website, we present a cookie consent banner
            allowing you to accept all cookies or reject non-essential cookies.
            Strictly necessary cookies do not require consent as they are
            essential for the site to operate.
          </p>
        </LegalSection>

        <LegalSection index="04" id="cookies-manage" title="Managing Your Preferences">
          <p>
            You can change or withdraw your consent at any time. Use the button
            below to reset your cookie preferences, or configure your browser to
            block or delete cookies. Please note that disabling certain cookies
            may affect the functionality of the website.
          </p>
          <button
            onClick={reset}
            data-testid="cookies-reset-btn"
            className="mt-4 inline-flex items-center bg-altin-ink text-white px-6 py-3 text-xs tracking-[0.2em] uppercase hover:bg-altin-gold transition-colors"
          >
            Reset Cookie Preferences
          </button>
        </LegalSection>

        <LegalSection index="05" id="cookies-contact" title="Contact">
          <p>
            For questions about our use of cookies, contact{" "}
            <a href="mailto:privacy@altin-link.com" className="text-altin-gold underline underline-offset-2">
              privacy@altin-link.com
            </a>
            .
          </p>
        </LegalSection>
      </LegalLayout>
    </>
  );
}
